import { clsx } from 'clsx';
import { Alert, Button } from '@/components/ui';
import { CargoListItem } from './CargoListItem';
import { CargoListSkeleton } from './CargoListSkeleton';
import { CargoListFiltersComponent } from './CargoListFilters';
import type { CargoItem, CargoListFilters } from '@/types/cargoList.types';
import { filterCargoItems } from '@/utils/cargoListUtils';
import { CARGO_SEGMENT_FILTER_ALL } from '@/constants/cargoList.constants';
import { Plus, FileText } from 'lucide-react';

interface CargoListProps {
  items: CargoItem[];
  filters: CargoListFilters;
  loading?: boolean;
  error?: string | null;
  refreshing?: boolean;
  /** Non-production only: rows are fabricated because the API read failed. */
  usingMockData?: boolean;
  onFiltersChange: (filters: Partial<CargoListFilters>) => void;
  onRefresh: () => void;
  onCargoClick?: (item: CargoItem) => void;
  onAddNew?: () => void;
}

export function CargoList({
  items,
  filters,
  loading = false,
  error = null,
  refreshing = false,
  usingMockData = false,
  onFiltersChange,
  onRefresh,
  onCargoClick,
  onAddNew,
}: CargoListProps) {
  // Filter items (preserve backend ordering to match Angular behavior)
  const filteredItems = filterCargoItems(items, filters);

  const hasResults = filteredItems.length > 0;
  // Drives the empty-state copy, so it has to account for every control that can
  // narrow the list - otherwise filtering to an empty segment reads as "no cargo
  // available" when there is plenty, just not of that size.
  const hasFilters =
    Boolean(filters.searchQuery) || filters.segmentFilter !== CARGO_SEGMENT_FILTER_ALL;

  // Only the first load gets a skeleton. A refresh (manual, or the background one
  // ProtectedRoute fires on every navigation into /cargo-list) keeps the rows on
  // screen - swapping them for a skeleton threw away already-cached data and made
  // a revisit look like a cold start.
  const showSkeleton = (loading || refreshing) && items.length === 0;
  const isRefreshingInPlace = refreshing && items.length > 0;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <CargoListFiltersComponent
        filters={filters}
        onFiltersChange={onFiltersChange}
        loading={loading}
      />

      {/* The list read failed and fell back to fabricated rows. Never reachable
          in production, where the read throws instead. */}
      {usingMockData && (
        <Alert variant="warning" title="Showing sample data">
          <p>
            The cargo API could not be reached, so these rows are placeholders — not real cargoes.
          </p>
          <Button variant="outline" size="sm" onClick={onRefresh} className="mt-2">
            Retry
          </Button>
        </Alert>
      )}

      {/* Error State. `fetchCargoList`/`refreshCargoList` leave `items` intact on
          failure, so when there are cached rows this sits above them rather than
          replacing a perfectly good list - a background refresh firing on every
          navigation into /cargo-list should not blank the page. */}
      {error && (
        <Alert
          variant="error"
          title={items.length > 0 ? 'Could not refresh cargo list' : 'Error Loading Cargo List'}
        >
          <p>{error}</p>
          {items.length > 0 && <p className="mt-1 text-sm">Showing the last data loaded.</p>}
          <Button variant="outline" size="sm" onClick={onRefresh} className="mt-2">
            Try Again
          </Button>
        </Alert>
      )}

      {/* Content */}
      {(!error || items.length > 0) && (
        <>
          {/* Inline Skeleton Loading */}
          {showSkeleton && <CargoListSkeleton />}

          {/* Results - List View */}
          {!showSkeleton && hasResults && (
            <div
              className={clsx(
                'flex flex-col gap-0.5 transition-opacity',
                isRefreshingInPlace && 'opacity-60'
              )}
              aria-busy={isRefreshingInPlace}
            >
              {filteredItems.map((item) => (
                <CargoListItem key={item.id} item={item} onClick={onCargoClick} />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!showSkeleton && !hasResults && (
            <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center dark:border-gray-700">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                {hasFilters ? 'No cargo found' : 'No cargo available'}
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {hasFilters
                  ? 'Try adjusting your filters or search criteria.'
                  : 'There are no cargo items available at the moment.'}
              </p>
              {!hasFilters && onAddNew && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onAddNew}
                  leftIcon={<Plus className="h-4 w-4" />}
                  className="mt-4"
                >
                  Add First Cargo
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
